import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { appConfig, checkAuthentictionOrRedirectToLogin, logger } from '$utils'
import useStockStore from './routes/stock/store'
import useCompanyStore from './routes/settings/company/store'
import usePayrollStore from './routes/time/payroll/store'

// This variable is independent of the React lifecycle and is used to force a
// re-mount of the React app when the AngularJS app tells us that the page
// content has changed, but the url has stayed the same.
let remountCounter = 0

/**
 * The AppRoot component is (currently) an invisible React component that is
 * permanently running and listening for any navigation events that have been
 * emitted by the legacy AngularJS app. It responds to these by triggering a
 * navigation action in the React app router - the end result being that any
 * pages that are powered by React end up being rendered.
 * @returns
 */
const AppRoot = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // The AngularJS app can navigate BEFORE the navigation listener below
  // exists (i.e. before the React app is loaded). This component only mounts
  // once the root route's readiness loader resolves, and a boot-time AngularJS
  // redirect (e.g. the zero-company signup redirect to the create-venue page)
  // dispatches '@@loaded/angularNavigation' while we're still blocked on that
  // loader. In those sorts of cases the event is missed forever and the React
  // router stays on the pre-redirect URL, wrongly rendering the page as a
  // non-React route.
  //
  // To handle this we check here when mounting the React app whether the current
  // browser URL is the same as the router's current location. If not, we navigate
  // the router to match the browser URL, forcing the correct page to render.
  useEffect(() => {
    // In standalone more (i.e. a pure React app) we don't need to mess about
    // with AngularJS navigation events, so we skip this logic entirely.
    if (appConfig.get<boolean>('standalone')) {
      return
    }

    const actualUrl = window.location.hash.substring(1)
    const routerUrl = location.pathname + location.search + location.hash
    if (actualUrl && actualUrl !== routerUrl) {
      logger.info(
        'Router is behind the actual URL on mount - syncing to %s',
        actualUrl
      )
      navigate(actualUrl, { replace: true })
    }
    // Deliberately run only on mount: this reconciles a navigation that
    // happened while the app was still booting. Later navigations are handled
    // by the '@@loaded/angularNavigation' listener below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleAngularNav = (ev: Event) => {
      const { to: destinationUrl, updatedPageContent } = (ev as CustomEvent)
        .detail
      logger.info(
        'Processing AngularJS navigation to %s %s',
        destinationUrl,
        updatedPageContent
          ? 'and we need to remount due to updated page conent'
          : ''
      )

      // TODO - remove this once the AngularJS app is no longer in use.
      //
      // We check the auth state on any AngularJS navigation to maintain backward
      // compatibility with what the AngularJS app did itself when it was
      // responsible for checking the users auth status. As we now 'own' this in
      // React, it's moved here to keep things in once place.
      checkAuthentictionOrRedirectToLogin()

      navigate(destinationUrl, { replace: true })
      if (updatedPageContent) {
        // When AngularJS has told us that the page content has been updated we
        // force a remount on the React app. This is expected to occur when the
        // url hasn't changed but the content is expected to - e.g. when a user
        // has updated the currently active company in the AngularJS app.
        remountCounter++
      }
    }

    // When the app context changes we need to force a remount of the React app
    // so that any company specific data is updated.
    const handleAppContextChange = (ev: Event) => {
      const { type } = (ev as CustomEvent).detail || {}
      // Reset module stores when switching companies so cached data
      // is not shown against the new company context
      if (type === 'company') {
        useStockStore.getState().resetSharedStockSlice()
        useCompanyStore.getState().resetCompanyPreferencesSlice()
        usePayrollStore.getState().resetPayrollStore()
      }
    }

    window.addEventListener('@@loaded/angularNavigation', handleAngularNav)
    window.addEventListener('@@loaded/appContextChange', handleAppContextChange)

    // Cleanup function removes listeners when effect re-runs or component unmounts
    return () => {
      window.removeEventListener('@@loaded/angularNavigation', handleAngularNav)
      window.removeEventListener(
        '@@loaded/appContextChange',
        handleAppContextChange
      )
    }
  }, [navigate])

  // The Outlet component is a special component that is used to render
  // any child routes that are defined in the router definition array -
  // in this case that's the pages we actually want to show injected into
  // the AngularJS parent app.
  return <Outlet key={remountCounter} />
}

export default AppRoot
